/**
 * Hooks for reading feature flags from environment variables or the server.
 *
 * Supports two backends:
 * - Vite env vars (VITE_*) — build-time flags, read from import.meta.env
 * - The web tier — runtime flags from `GET /v2/me/flags` (the one evaluator over the PostHog
 *   rules in Redis). posthog-js is analytics only; it never evaluates a flag here.
 */

import { useMemo } from 'react'
import {
  POSTHOG_FLAG_DEFAULTS as GENERATED_FLAG_DEFAULTS,
  POSTHOG_FLAG_VARIANT_DEFAULTS,
} from '@/generated/flagDefaults.generated'
import { useFeatureFlagsStore } from '@/store/featureFlagsStore'
import type { ServerFlags } from '@/store/featureFlagsSlice'

/**
 * Known environment-based feature flags in the application
 */
export type FeatureFlagName =
  | 'VITE_USE_VERCEL_AI_CHAT'
  | 'VITE_UIV2'

/**
 * Known PostHog-managed feature flags
 */
export type ApiFeatureFlagName = 'restrict_upload_to_internal_users'

/**
 * Defaults for PostHog-managed flags (used before the server's flags load or when it has no answer).
 */
const POSTHOG_FLAG_DEFAULTS: Readonly<Record<string, boolean>> = GENERATED_FLAG_DEFAULTS

/**
 * Read a feature flag from environment variables
 */
export function useFeatureFlag(flagName: FeatureFlagName): boolean {
  return useMemo(() => {
    const value = import.meta.env[flagName]

    if (typeof value === 'string') {
      return value.toLowerCase() === 'true' || value === '1'
    }

    if (typeof value === 'boolean') {
      return value
    }

    return false
  }, [flagName])
}

/**
 * Check if running in development mode
 */
export function useIsDevelopment(): boolean {
  return import.meta.env.DEV === true
}

/**
 * Check if Vercel AI Chat feature is enabled
 */
export function useVercelAIChat(): boolean {
  return useFeatureFlag('VITE_USE_VERCEL_AI_CHAT')
}

/** The caller's server-evaluated flags, or null until they load (the store drops another user's). */
function useServerFlags(): ServerFlags | null {
  return useFeatureFlagsStore((s) => s.serverFlags)
}

/**
 * Hook for reading a PostHog-managed boolean feature flag.
 *
 * The value comes from the web tier (`GET /v2/me/flags`, loaded once after login into the store
 * and refreshed on tab focus): the one evaluator every surface shares, not the browser. Until it
 * loads, or for a flag it does not list, the generated registry default (then `defaultValue`)
 * applies. A variant flag reads as on unless it answers the string `false`, as
 * `posthog.isFeatureEnabled` did. `source` is `server` or `default`, for diagnostics only.
 */
export function useApiFeatureFlag(
  flagName: ApiFeatureFlagName | string,
  defaultValue: boolean = false
): { enabled: boolean; loading: boolean; source: string } {
  const flags = useServerFlags()
  const value = flags?.[flagName]
  const fallback = POSTHOG_FLAG_DEFAULTS[flagName] ?? defaultValue
  if (value === undefined) {
    return { enabled: fallback, loading: flags === null, source: 'default' }
  }
  return {
    enabled: typeof value === 'boolean' ? value : value !== 'false',
    loading: false,
    source: 'server',
  }
}

/**
 * Hook for reading a PostHog-managed variant flag: the variant string. Before the flags load,
 * or when the server does not answer with a string, the generated registry default.
 */
export function useFeatureFlagVariant(flagName: string): {
  variant: string
  loading: boolean
  source: string
} {
  const flags = useServerFlags()
  const value = flags?.[flagName]
  const fallback = POSTHOG_FLAG_VARIANT_DEFAULTS[flagName] ?? ''
  if (typeof value !== 'string') {
    return { variant: fallback, loading: flags === null, source: 'default' }
  }
  return { variant: value, loading: false, source: 'server' }
}

/**
 * Get a feature flag value synchronously (not a hook)
 * Use this in non-React contexts
 */
export function getFeatureFlagValue(flagName: FeatureFlagName): boolean {
  const value = import.meta.env[flagName]

  if (typeof value === 'string') {
    return value.toLowerCase() === 'true' || value === '1'
  }

  if (typeof value === 'boolean') {
    return value
  }

  return false
}

export default useFeatureFlag
